import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useSEO from '../hooks/useSEO';
import { 
  CheckCircle2, 
  Phone, 
  Calendar, 
  Users, 
  Truck, 
  Star, 
  ChevronDown, 
  ChevronUp,
  Utensils,
  ArrowRight,
  Mail,
  X
} from 'lucide-react';

const Home = () => {
  useSEO(
    'Catering & Mellemøstlig Street Food | House of Falafel',
    'Få autentisk mellemøstlig catering til dit næste event. Frisklavet fra bunden til op til 500 gæster. Book mobil catering eller ring 22907030 for tilbud!'
  );

  const [menuItems, setMenuItems] = useState([]);
  const [cateringPackages, setCateringPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [showAllGallery, setShowAllGallery] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    {
      url: "https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/0bb61f88-47e1-40b9-8015-7a0acb91e52a.png",
      title: "Catering Setup",
      category: "Events"
    },
    {
      url: "https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/13d6e302-5d7a-4a16-a437-75bd791d86b7.png",
      title: "Crispy Kylling Burger",
      category: "Menu"
    },
    {
      url: "https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/6a5bbbe2-6842-48b8-a2d4-ca8fc06db1ed.png",
      title: "Kylling Nuggets & Pomfrits",
      category: "Børnemenu"
    }
  ];

  const visibleImages = showAllGallery ? galleryImages : galleryImages.slice(0, 3);

  const categoryOrder = [
    "Falafel",
    "Samosa",
    "Shawarma",
    "Kebab Shawarma",
    "Crispy",
    "Burger",
    "Børneretter",
    "Pomfrits",
    "Drikkevarer",
    "Tilbehør"
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuRes, cateringRes, testimonialRes, faqRes] = await Promise.all([
          fetch('https://api.dev.auxo.dk/public/content/menu_items'),
          fetch('https://api.dev.auxo.dk/public/content/catering_packages'),
          fetch('https://api.dev.auxo.dk/public/content/testimonials'),
          fetch('https://api.dev.auxo.dk/public/content/faqs')
        ]);

        const [menu, catering, testimonial, faq] = await Promise.all([
          menuRes.json(),
          cateringRes.json(),
          testimonialRes.json(),
          faqRes.json()
        ]);

        setMenuItems(menu);
        setCateringPackages(catering);
        setTestimonials(testimonial);
        setFaqs(faq);
      } catch (error) {
        console.error('Error fetching CMS content:', error);
      }
    };

    fetchData();
  }, []);

  const trustBadges = [
    { icon: <CheckCircle2 className="h-8 w-8" />, text: "Frisklavet fra bunden" },
    { icon: <Users className="h-8 w-8" />, text: "Op til 500 gæster" },
    { icon: <Truck className="h-8 w-8" />, text: "Mobil catering" },
    { icon: <Star className="h-8 w-8" />, text: "Autentisk smag" }
  ];

  const categories = categoryOrder.filter(cat => 
    menuItems.some(item => item.category === cat)
  );

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0 bg-zinc-950">
          <img 
            src="https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/07146c37-5592-48ba-8df4-c4fb4b0c81cc.png" 
            alt="Autentisk mad" 
            className="w-full h-full object-cover opacity-60 scale-105 animate-slow-zoom"
          />
          {/* Orange gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-orange-600/80 via-zinc-950/40 to-transparent"></div>
          <div className="absolute inset-0 bg-black/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-16 md:pt-48 lg:pt-56 lg:pb-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Content */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">
              Mad til dit næste event
            </h1>
            <p className="text-xl text-white mb-10 leading-relaxed max-w-2xl mx-auto">
              Autentisk mellemøstlig street food lavet fra bunden – serveret direkte til festivaler, firmafester, konfirmationer, fødselsdage og private arrangementer.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-16 justify-center">
              <Link to="/kontakt" className="bg-amber-600 hover:bg-amber-700 hover:-translate-y-1 text-white px-8 py-4 rounded-full text-lg font-bold transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20">
                Book catering nu
              </Link>
              <a href="tel:22907030" className="border border-white text-white px-8 py-4 rounded-full text-lg font-bold transition-all hover:-translate-y-1 flex items-center justify-center gap-2">
                <Phone className="h-5 w-5" />
                Ring på 22 90 70 30
              </a>
            </div>

            {/* Trust Badges */}
            <div className="pt-8 border-t border-white max-w-3xl mx-auto" data-dev-id="src/pages/Home.jsx:trust-badges-hero">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {trustBadges.map((badge, idx) => (
                  <div key={idx} className="flex flex-col items-center gap-3 text-white font-medium">
                    <span>{badge.icon}</span>
                    <span className="text-sm md:text-base">{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Animated Scroll Arrow */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.5, 
            repeat: Infinity, 
            repeatType: "reverse",
            ease: "easeInOut"
          }}
          className="hidden lg:block absolute bottom-10 left-1/2 -translate-x-1/2 z-20 text-zinc-400 cursor-pointer hover:text-zinc-900 transition-colors"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <ChevronDown size={48} strokeWidth={1} />
        </motion.div>
      </section>

      {/* Event Types */}
      <section className="pt-12 pb-24 bg-zinc-950 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-16 text-white">Perfekt til alle typer <span className="text-amber-500">events</span></h2>
          <div className="flex flex-col md:flex-row flex-wrap justify-center items-center">
            {[
              "Festivaler", "Food truck-events", "Firmafester", 
              "Konfirmationer", "Fødselsdage", "Private arrangementer",
              "Store selskaber"
            ].map((event, idx, arr) => (
              <div key={idx} className={`w-full md:w-auto px-8 py-4 border-white/10 ${idx !== arr.length - 1 ? 'border-b md:border-b-0 md:border-r' : ''}`}>
                <p className="text-xl md:text-2xl font-bold text-zinc-200">{event}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 text-zinc-500 text-lg max-w-2xl mx-auto">
            Uanset om du planlægger et mindre privat arrangement eller et større event med mange gæster, tilpasser vi setup, menu og servering efter behov.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-black overflow-hidden border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2 py-20 md:py-32 order-2 lg:order-1">
              <div className="max-w-xl">
                <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight text-white">
                  Gør dit arrangement nemt, lækkert og mindeværdigt
                </h2>
                <div className="space-y-6 text-white text-lg leading-relaxed">
                  <p>
                    Hos House of Falafel får du mere end bare mad. Du får en mobil madoplevelse, hvor duften, smagen og serveringen skaber stemning fra første gæst stiller sig i kø.
                  </p>
                  <p>
                    Vi sørger for frisklavet falafel, shawarma, burger, nuggets og hjemmelavede råvarer, så du kan fokusere på dine gæster.
                  </p>
                </div>
                <div className="mt-10">
                  <Link to="/kontakt" className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-1 shadow-lg shadow-amber-600/20 group">
                    Få et uforpligtende tilbud 
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 h-[400px] lg:h-[500px] relative order-1 lg:order-2 w-full">
              <img 
                src="https://assets.auxodev.dk/assets/76e41030-ec38-42da-9e63-2e6acd74222c/07146c37-5592-48ba-8df4-c4fb4b0c81cc.png" 
                alt="Frisklavet falafel" 
                className="w-full h-full object-cover opacity-80 rounded-3xl border border-white/10"
              />
              <div className="absolute bottom-8 left-8 bg-amber-600 p-6 md:p-8 rounded-2xl shadow-2xl">
                <p className="text-3xl md:text-4xl font-black text-white">100%</p>
                <p className="text-white font-medium">Hjemmelavet</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Catering Menu */}
      <section id="catering" className="py-24 bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Vælg din cateringmenu</h2>
            <p className="text-zinc-400">Vi tilbyder fleksible løsninger til ethvert budget</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cateringPackages.map((pkg) => (
              <div key={pkg.id} className="bg-black border border-zinc-800 rounded-2xl p-8 flex flex-col h-full">
                <h3 className="text-2xl font-bold mb-2">{pkg.name.da}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-black text-amber-500">{pkg.price_per_person} kr.</span>
                  <span className="text-zinc-500">pr. kuvert</span>
                </div>
                <p className="text-zinc-400 mb-8 flex-grow">
                  {pkg.description.da}
                </p>
                <Link to="/kontakt" className="w-full bg-amber-600 hover:bg-amber-700 hover:-translate-y-1 text-white py-4 rounded-xl font-bold transition-all text-center">
                  Book nu
                </Link>
              </div>
            ))}
            {/* Special Firmaaftale Card */}
            <div className="bg-amber-600 rounded-2xl p-8 flex flex-col h-full text-white shadow-xl shadow-amber-900/20">
              <h3 className="text-2xl font-bold mb-2">Firma-aftale</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black">1.500 kr.</span>
                <span className="text-amber-100">engangsydelse</span>
              </div>
              <p className="text-amber-500 bg-white inline-block px-3 py-1 rounded-full text-sm font-bold mb-4 self-start">
                10% rabat til medarbejdere
              </p>
              <p className="text-amber-50 text-lg mb-8 flex-grow">
                Lad dine medarbejdere købe mad med 10% rabat direkte fra vores vogn.
              </p>
              <Link to="/kontakt" className="w-full bg-white text-amber-600 hover:bg-amber-50 hover:-translate-y-1 py-4 rounded-xl font-bold transition-all text-center">
                Hør mere om firmaaftale
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* In-store Menu */}
      <section id="menu" className="py-24 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Menu i forretningen</h2>
            <p className="text-zinc-400">Besøg os og smag vores frisklavede specialiteter</p>
          </div>

          <div className="columns-1 md:columns-2 lg:columns-3 gap-12 [column-fill:_balance]">
            {categories.map((category) => {
              const categoryItems = menuItems.filter(item => item.category === category);
              
              if (category === "Tilbehør") {
                const standardItems = categoryItems.filter(item => item.price !== 10);
                const extraItems = categoryItems.filter(item => item.price === 10);
                
                return (
                  <div key={category} className="break-inside-avoid mb-12 flex flex-col">
                    <h3 className="text-2xl font-bold text-amber-500 mb-6 border-b border-zinc-900 pb-2">Salat</h3>
                    
                    {/* Standard Tilbehør */}
                    <div className="space-y-6 mb-8">
                      {standardItems.map((item) => (
                        <div key={item.id} className="flex justify-between items-start group">
                          <div>
                            <h4 className="font-bold text-lg group-hover:text-amber-500 transition-colors">{item.name.da}</h4>
                            {item.description?.da && <p className="text-zinc-500 text-sm">{item.description.da}</p>}
                          </div>
                          <span className="font-bold text-amber-500 shrink-0 ml-4">{item.price} kr.</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Ekstra Sub-section */}
                    {extraItems.length > 0 && (
                      <div className="pt-6 border-t border-zinc-900">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-xl font-bold text-zinc-300">Ekstra</h4>
                          <span className="text-xs font-semibold text-amber-500 bg-amber-500/10 px-2.5 py-1 rounded-full border border-amber-500/20">
                            10 kr. pr. stk.
                          </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                          {extraItems.map((item) => (
                            <div key={item.id} className="flex justify-between items-center py-1.5 border-b border-zinc-900/30 group">
                              <span className="text-zinc-400 group-hover:text-amber-500 transition-colors text-sm font-medium">
                                {item.name.da}
                              </span>
                              <span className="text-zinc-600 text-xs font-semibold group-hover:text-amber-500 transition-colors">
                                {item.price} kr.
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <div key={category} className="break-inside-avoid mb-12">
                  <h3 className="text-2xl font-bold text-amber-500 mb-6 border-b border-zinc-900 pb-2">{category}</h3>
                  <div className="space-y-6">
                    {categoryItems.map((item) => (
                      <div key={item.id} className="flex justify-between items-start group">
                        <div>
                          <h4 className="font-bold text-lg group-hover:text-amber-500 transition-colors">{item.name.da}</h4>
                          {item.description?.da && <p className="text-zinc-500 text-sm">{item.description.da}</p>}
                        </div>
                        <span className="font-bold text-amber-500 shrink-0 ml-4">{item.price} kr.</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black text-white border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Det siger vores <span className="text-amber-500">kunder</span></h2>
            <p className="text-zinc-400">Vi lever af glade gæster og gode oplevelser</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-zinc-900/50 p-8 rounded-2xl border border-zinc-800 relative">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="text-amber-500 h-5 w-5 fill-amber-500" />
                  ))}
                </div>
                <p className="text-zinc-300 italic mb-6 text-lg leading-relaxed">“{t.quote.da}”</p>
                <div className="flex items-center gap-4">
                  <div>
                    <p className="font-bold text-white">{t.author_name}</p>
                    <p className="text-zinc-500 text-sm">{t.event_type.da}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery, FAQ & Final Contact CTA */}
      <section className="py-24 bg-black text-white border-t border-zinc-900">
        <div id="galleri" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-32">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Se maden i aktion</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Vi går aldrig på kompromis med kvaliteten. Se hvordan vi tilbereder vores autentiske retter og skaber stemning til events.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {visibleImages.map((image, index) => (
                <motion.div
                  key={image.url}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative aspect-square overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => setShowAllGallery(!showAllGallery)}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-zinc-800 hover:border-white hover:-translate-y-1 transition-all font-bold"
            >
              {showAllGallery ? (
                <>Vis færre billeder <ChevronUp className="h-5 w-5" /></>
              ) : (
                <>Se flere billeder <ChevronDown className="h-5 w-5" /></>
              )}
            </button>
          </div>
        </div>

        <div id="faq" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-32">
          <h2 className="text-4xl font-bold mb-12 text-center">Ofte stillede spørgsmål</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={faq.id} className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
                <button 
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-8 py-6 flex justify-between items-center text-left hover:bg-zinc-800 transition-colors"
                >
                  <span className="font-bold text-lg">{faq.question.da}</span>
                  {openFaq === idx ? <ChevronUp className="text-amber-500" /> : <ChevronDown className="text-zinc-500" />}
                </button>
                {openFaq === idx && (
                  <div className="px-8 pt-4 pb-6 text-zinc-400 leading-relaxed border-t border-zinc-800/50">
                    {faq.answer.da}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div id="kontakt" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-24 border-t border-zinc-900">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-8">Klar til at bestille?</h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="tel:22907030" className="bg-amber-600 text-white px-10 py-5 rounded-full text-xl font-black hover:bg-amber-700 hover:-translate-y-1 transition-all flex items-center justify-center gap-3 shadow-xl shadow-amber-600/20">
              <Phone className="h-6 w-6" />
              Ring nu: 22 90 70 30
            </a>
            <a href="mailto:houseoffalafel2023@icloud.com" className="bg-white text-zinc-900 px-10 py-5 rounded-full text-xl font-black hover:bg-zinc-100 hover:-translate-y-1 transition-all flex items-center justify-center gap-3">
              <Mail className="h-6 w-6" />
              Send email
            </a>
          </div>
          <div className="mt-12 text-zinc-500 font-medium">
            <p>House of Falafel · CVR: 44081652</p>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center"
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-white hover:text-amber-500 transition-colors p-2"
              >
                <X size={32} />
              </button>
              <img
                src={selectedImage.url}
                alt={selectedImage.title}
                className="w-full h-full object-contain rounded-xl shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Home;
